"use client";

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ProfessorProfile from '@/app/Components/SIRS/ProfessorProfile';
import CourseRatings from '@/app/Components/SIRS/CourseRatings';
import SIRSSearch from '@/app/Components/SIRS/SIRSSearch';
import DepartmentRatings from '@/app/Components/SIRS/DepartmentRatings';
import {
  OperationalHeader,
  OperationalPage,
  OperationalStatus,
  Surface,
} from '@/app/Components/OperationalPage';
import { Button } from '@/app/Components/Button';
import { InfoTooltip } from '@/app/Components/InfoTooltip';
import type { PageArchetype } from '@/app/lib/routePresentation';

export type ViewMode = 'search' | 'professor' | 'course' | 'department';
const SIRS_PAGE_ARCHETYPE = 'directory' satisfies PageArchetype;

export default function SIRSPageClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [viewMode, setViewMode] = useState<ViewMode>('search');
  const [selectedProfessor, setSelectedProfessor] = useState<string | null>(null);
  const [selectedCourse, setSelectedCourse] = useState<string | null>(null);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Handle URL parameters
  useEffect(() => {
    const prof = searchParams.get('professor');
    const course = searchParams.get('course');
    const dept = searchParams.get('department');
    const q = searchParams.get('q');

    if (prof) {
      setViewMode('professor');
      setSelectedProfessor(prof);
    } else if (course) {
      setViewMode('course');
      setSelectedCourse(course);
    } else if (dept) {
      setViewMode('department');
      setSelectedDepartment(dept);
    } else {
      setViewMode('search');
    }

    if (q) {
      setSearchQuery(q);
    }
  }, [searchParams]);

  const handleProfessorSelect = (name: string) => {
    setSelectedProfessor(name);
    setViewMode('professor');
    router.push(`/sirs?professor=${encodeURIComponent(name)}`);
  };

  const handleCourseSelect = (courseId: string) => {
    setSelectedCourse(courseId);
    setViewMode('course');
    router.push(`/sirs?course=${encodeURIComponent(courseId)}`);
  };

  const handleDepartmentSelect = (deptCode: string) => {
    setSelectedDepartment(deptCode);
    setViewMode('department');
    router.push(`/sirs?department=${encodeURIComponent(deptCode)}`);
  };

  const handleBack = () => {
    setViewMode('search');
    setSelectedProfessor(null);
    setSelectedCourse(null);
    setSelectedDepartment(null);
    router.push('/sirs');
  };

  const contextLabel =
    viewMode === 'professor' && selectedProfessor
      ? selectedProfessor
      : viewMode === 'course' && selectedCourse
        ? selectedCourse
        : viewMode === 'department' && selectedDepartment
          ? selectedDepartment
          : 'Browse Rutgers SIRS data';

  return (
    <OperationalPage className="max-w-7xl" data-page-archetype={SIRS_PAGE_ARCHETYPE}>
      <OperationalHeader
        title="SIRS Ratings"
        description={
          <span className="-my-3 inline-flex items-center gap-1">
            Historical Rutgers student surveys
            <InfoTooltip title="Using SIRS ratings" text="Compare the course, term, class size, response rate and question behind each average. These historical survey records describe available evidence; they do not establish current teaching availability or predict an individual student's experience." />
          </span>
        }
        actions={
          viewMode !== 'search' ? (
            <>
              <OperationalStatus>{contextLabel}</OperationalStatus>
              <Button variant="secondary" size="sm" onClick={handleBack} aria-label="Back to search">
                <ArrowLeftIcon className="h-3.5 w-3.5" />
                Back
              </Button>
            </>
          ) : undefined
        }
      />

      {viewMode === 'search' ? (
        <SIRSSearch
          onProfessorSelect={handleProfessorSelect}
          onCourseSelect={handleCourseSelect}
          onDepartmentSelect={handleDepartmentSelect}
          initialQuery={searchQuery}
        />
      ) : (
        <Surface variant="feature">
          {viewMode === 'professor' && selectedProfessor && (
            <ProfessorProfile
              professorName={selectedProfessor}
              onBack={handleBack}
              onDepartmentSelect={handleDepartmentSelect}
            />
          )}

          {viewMode === 'course' && selectedCourse && (
            <CourseRatings
              courseId={selectedCourse}
              onBack={handleBack}
              onProfessorSelect={handleProfessorSelect}
              onDepartmentSelect={handleDepartmentSelect}
            />
          )}

          {viewMode === 'department' && selectedDepartment && (
            <DepartmentRatings
              departmentCode={selectedDepartment}
              onBack={handleBack}
              onCourseSelect={handleCourseSelect}
              onProfessorSelect={handleProfessorSelect}
            />
          )}
        </Surface>
      )}
    </OperationalPage>
  );
}
